import React, { useEffect, useState, useRef, lazy } from 'react';
interface LazyMountProps {
  children: React.ReactNode;
  /** Approx. height reserved before mount to avoid layout shift (px). */
  minHeight?: number;
  /** How far ahead of the viewport to start mounting. */
  rootMargin?: string;
  /** Optional id applied to the always-present wrapper (anchor target). */
  id?: string;
}
/**
 * Renders a lightweight placeholder until the section approaches the viewport,
 * then mounts its children. This defers the download + parse + execution of
 * each section's lazy chunk until it's actually needed, cutting initial JS cost.
 */
export function LazyMount({
  children,
  minHeight = 600,
  rootMargin = '600px',
  id
}: LazyMountProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show || typeof window === 'undefined') return;
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setShow(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setShow(true);
        observer.disconnect();
      }
    }, {
      rootMargin
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [show, rootMargin]);
  return <div ref={ref} id={id} style={show ? undefined : {
    minHeight
  }} data-id="element-191">
      {show ? children : null}
    </div>;
}